import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from '@src/languages';
import Tooltip from '@src/components/Tooltip';
import sanitizeHtml from 'sanitize-html';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { getPriceText } from '@src/services/payment';
import {
  Box,
  Button,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { checkVietNam } from '@src/utils/checkCountry';
import { NEW_SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from '@src/styles/config';
import theme from '@src/styles/theme';
import { getPlanChange } from '@src/services/package';
import { StyledPaymentFooter } from './index.style';

const PaymentFooter = ({
  actualPrice,
  isAutoRenew,
  totalPrice = 0,
  onCreateOrder,
  loadingGetPreviewRolloverCredits,
}) => {
  const { t } = useTranslation();
  const { language } = i18n;

  const user = useSelector((state) => state.auth.user);
  const { openSidebar } = useSelector((state) => state.layout);
  const { package: packageState } = useSelector((state) => state.order);
  const { usingPackage } = useSelector((state) => state.user);
  const [openPopupMinPaymentAmount, setOpenPopupMinPaymentAmount] =
    useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleShowPopupMinPaymentAmount = () => {
    if (!isMobile) return;
    setOpenPopupMinPaymentAmount(true);
  };

  const isVietNam = checkVietNam();
  const { paymentMethod } = useSelector((state) => state.order);
  const minPaymentAmountByLanguage = isVietNam
    ? paymentMethod?.minPaymentAmount?.vnd
    : paymentMethod?.minPaymentAmount?.usd;

  const [isLoading, setIsLoading] = useState(true);
  const skeletonLoading = isLoading || loadingGetPreviewRolloverCredits;

  const getSidebarWidth = () => {
    let sidebarWidth = 0;
    sidebarWidth = openSidebar ? NEW_SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED;

    return sidebarWidth;
  };

  useEffect(() => {
    if (Object.keys(paymentMethod).length === 0 || !packageState) {
      setIsLoading(true);
    } else setIsLoading(false);
  }, [paymentMethod, packageState]);

  return (
    <StyledPaymentFooter
      autorenew={isAutoRenew}
      sidebarwidth={getSidebarWidth()}
    >
      <Box>
        <Typography
          data-id="view-payment-policy-link"
          dangerouslySetInnerHTML={{ __html: t('paymentTerm') }}
        />
      </Box>

      <div className="container">
        {totalPrice >= 0 &&
          (skeletonLoading ? (
            <Skeleton
              animation="wave"
              height={30}
              width="30%"
              className="skeleton"
              style={{ marginRight: '12px' }}
            />
          ) : (
            <div className="price">
              <div className="price-text">{t('total')}:</div>
              {!isVietNam && (
                <span className="discount-unit-price text-16">$</span>
              )}
              {isVietNam && (
                <span className="discount-unit-price text-16 underline">đ</span>
              )}
              <div className="">{totalPrice?.toLocaleString(language)}</div>
              {!packageState?.isTopUp &&
                actualPrice < (minPaymentAmountByLanguage || 0) && (
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(
                            t('minimumPayment', {
                              minimunPrice: getPriceText(
                                minPaymentAmountByLanguage,
                                isVietNam,
                                language,
                              ),
                              actualPrice: getPriceText(
                                actualPrice,
                                isVietNam,
                                language,
                              ),
                            }),
                            {
                              allowedTags: ['strong', 'span'],
                              allowedAttributes: {
                                '*': ['style'],
                              },
                            },
                          ),
                        }}
                      />
                    }
                  >
                    <InfoRoundedIcon
                      onClick={handleShowPopupMinPaymentAmount}
                      className="info-rounded-icon"
                    />
                  </Tooltip>
                )}
            </div>
          ))}

        <Button
          className="agreeToPayButton"
          variant="contained"
          disabled={isLoading || loadingGetPreviewRolloverCredits}
          onClick={onCreateOrder}
          data-id="button-payment"
          data-product={packageState?.type}
          data-package-code={packageState?.code}
          data-plan-change={getPlanChange({
            packageState,
            usingPackage,
            user,
          })}
        >
          {t('payment')}
        </Button>
      </div>

      <NotificationDialogV3
        name={t('noticePopup')}
        title={t('noticePopup')}
        description={sanitizeHtml(
          t('minimumPayment', {
            minimunPrice: getPriceText(
              minPaymentAmountByLanguage,
              isVietNam,
              language,
            ),
            actualPrice: getPriceText(actualPrice, isVietNam, language),
          }),
          {
            allowedTags: ['strong', 'span'],
            allowedAttributes: {
              '*': ['style'],
            },
          },
        )}
        open={openPopupMinPaymentAmount}
        onClose={() => setOpenPopupMinPaymentAmount(false)}
        isAutoRenew
        variant="null"
        actionComponent={
          <Button
            onClick={() => setOpenPopupMinPaymentAmount(false)}
            className="btn-close-popup"
            variant="contained"
          >
            {t('gotIt')}
          </Button>
        }
      />
    </StyledPaymentFooter>
  );
};

export default PaymentFooter;
